import { useState } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import { ChevronDownIcon, ChevronUpIcon } from '@mote/assets/icons'
import { Button } from '@mote/ui'
import styled, { css, th } from '@xstyled/styled-components'

export interface NavigationalLinkConfig {
  path: string
  label: string
  activeWhen?: string[]
}

export interface NavigationalLinkProps extends NavigationalLinkConfig {
  childPaths?: NavigationalLinkConfig[]
  badgeText?: string | number
  isExternal?: boolean
}

export function NavigationalLink(props: NavigationalLinkProps) {
  const badge = props.badgeText ? (
    <NotificationsBadge>{props.badgeText}</NotificationsBadge>
  ) : null

  if (props.childPaths) {
    return (
      <NavigationalLinkWithChildPaths
        {...props}
        childPaths={props.childPaths}
      />
    )
  }

  if (props.isExternal) {
    return <StyledA href={props.path}>{props.label}</StyledA>
  }

  return (
    <StyledNavLink to={props.path}>
      {props.label}
      {badge}
    </StyledNavLink>
  )
}

function NavigationalLinkWithChildPaths(
  props: NavigationalLinkProps & { childPaths: NavigationalLinkConfig[] }
) {
  const match = useMatch({ path: props.path, end: false })
  const [isOpen, setIsOpen] = useState(Boolean(match))

  const paths = props.childPaths.map(childPath => (
    <NavigationalLink
      key={childPath.path}
      path={childPath.path}
      label={childPath.label}
    />
  ))

  return (
    <div>
      <Button
        //@ts-expect-error
        block
        style={{ justifyContent: 'space-between' }}
        variant={'soft'}
        text={props.label}
        onClick={() => {
          setIsOpen(!isOpen)
        }}
        iconPosition={'right'}
        icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      />
      {isOpen ? (
        <StyledChildPathsWrapper>{paths}</StyledChildPathsWrapper>
      ) : null}
    </div>
  )
}

const StyledChildPathsWrapper = styled.div({
  marginLeft: 10
})

const commonNavLinkStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: black;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 120%;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
  padding: 0 16px;
  transition: all 150ms ease-in;
  background: transparent;
  border-radius: 4px;

  &:hover,
  &:focus-visible {
    background: ${th.color('grey.l3')};
  }
`

const StyledNavLink = styled(NavLink)`
  ${commonNavLinkStyles};
  &.active {
    color: ${th.color('primary.base')};
    background: ${th.color('grey.l3')};
  }
`

const StyledA = styled.a`
  ${commonNavLinkStyles};
`

const NotificationsBadge = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  padding: 4px;
  width: 24px;
  height: 24px;
  border: 1.5px solid ${th.color('grey.l2')};
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
  color: ${th.color('primary.base')};
`
